import { AuthModel, UserModel, UserPermissions } from '../api/model/model';
import * as authHelper from '../utils/token';
import { getRoleFromToken, getUserPermissions } from '../utils/token';
import { UserService } from 'api/generated';
import { LayoutSplashScreen } from 'components/layout/splash-screen/splash-screen.context';
import { Page, Role } from 'features/shared/model';
import { Dispatch, FC, SetStateAction, createContext, useContext, useEffect, useRef, useState } from 'react';
import { WithChildren } from 'ts-types';

export type Access = 'create' | 'read' | 'update' | 'delete';

type AuthContextProps = {
  auth: AuthModel | undefined;
  saveAuth: (auth: AuthModel | undefined) => void;
  currentUser: UserModel | undefined;
  currentPermissions: UserPermissions | undefined;
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>;
  setCurrentPermissions: Dispatch<SetStateAction<UserPermissions | undefined>>;
  isGranted: (page: Page, access: Access) => boolean;
  logout: () => void;
};

const initAuthContextPropsState: AuthContextProps = {
  auth: authHelper.getAuth(),
  saveAuth: () => {
    // empty
  },
  currentUser: undefined,
  currentPermissions: undefined,
  setCurrentUser: () => {
    // empty
  },
  setCurrentPermissions: () => {
    // empty
  },
  isGranted: () => {
    return false;
  },
  logout: () => {
    // empty
  }
};

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState);

const useAuth = () => {
  return useContext(AuthContext);
};

const AuthProvider: FC<WithChildren> = ({ children }) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth());
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>();
  const [currentPermissions, setCurrentPermissions] = useState<UserPermissions | undefined>();

  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth);
    if (auth) {
      authHelper.setAuth(auth);
    } else {
      authHelper.removeAuth();
    }
  };

  const isGranted = (page: Page, access: Access) => {
    const pageAccess = currentPermissions?.find((item) => item.page == page);

    if (!pageAccess) {
      return false;
    }

    switch (access) {
      case 'create':
        return pageAccess.access.create;
      case 'read':
        return pageAccess.access.read;
      case 'update':
        return pageAccess.access.update;
      case 'delete':
        return pageAccess.access.delete;
      default:
        return false;
    }
  };

  const logout = () => {
    saveAuth(undefined);
    setCurrentUser(undefined);
    setCurrentPermissions(undefined);
  };

  return (
    <AuthContext.Provider
      value={{
        auth,
        saveAuth,
        currentUser,
        setCurrentUser,
        currentPermissions,
        setCurrentPermissions,
        logout,
        isGranted
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const AuthInit: FC<WithChildren> = ({ children }) => {
  const { auth, logout, setCurrentUser, setCurrentPermissions } = useAuth();
  const didRequest = useRef(false);
  const [showSplashScreen, setShowSplashScreen] = useState(true);

  useEffect(() => {
    const requestUser = (apiToken: string) => {
      if (!didRequest.current) {
        const role: Role | null = getRoleFromToken(apiToken);
        setCurrentUser(auth as UserModel);
        UserService.getUsersPermissions(role as number).then((permissions) => {
          setCurrentPermissions(getUserPermissions(permissions));
        });
      }
      return () => (didRequest.current = true);
    };
    if (auth && auth.token) {
      requestUser(auth.token);
    } else {
      logout();
    }
    setShowSplashScreen(false);
  }, []);

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>;
};

export { AuthProvider, AuthInit, useAuth };
